import { useEffect, useState } from "react";
import { settingsAPI } from "@/lib/api";

/**
 * HeadCodeInjector - Fetches site settings and injects header/footer code
 * This component should be rendered once at the app level
 * Properly handles meta tags, scripts, and other HTML elements
 */
const HeadCodeInjector = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch site settings:", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!settings) return;

    const injectedElements = [];

    // Inject header code
    if (settings.header_code) {
      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = settings.header_code;
      
      // Process each child element
      Array.from(tempDiv.children).forEach((element) => {
        const tagName = element.tagName.toLowerCase();
        
        if (tagName === "meta") {
          // Handle meta tags - add directly to head
          const meta = document.createElement("meta");
          Array.from(element.attributes).forEach((attr) => {
            meta.setAttribute(attr.name, attr.value);
          });
          // Add a marker to identify injected meta tags
          meta.setAttribute("data-injected", "true");
          document.head.appendChild(meta);
          injectedElements.push(meta);
        } else if (tagName === "link") {
          // Handle link tags
          const link = document.createElement("link");
          Array.from(element.attributes).forEach((attr) => {
            link.setAttribute(attr.name, attr.value);
          });
          link.setAttribute("data-injected", "true");
          document.head.appendChild(link);
          injectedElements.push(link);
        } else if (tagName === "script") {
          // Handle script tags
          const script = document.createElement("script");
          Array.from(element.attributes).forEach((attr) => {
            script.setAttribute(attr.name, attr.value);
          });
          if (element.textContent) {
            script.textContent = element.textContent;
          }
          script.setAttribute("data-injected", "true");
          document.head.appendChild(script);
          injectedElements.push(script);
        } else if (tagName === "style") {
          // Handle style tags
          const style = document.createElement("style");
          style.textContent = element.textContent;
          style.setAttribute("data-injected", "true");
          document.head.appendChild(style);
          injectedElements.push(style);
        } else {
          // For other elements, add them as-is
          const clone = element.cloneNode(true);
          clone.setAttribute("data-injected", "true");
          document.head.appendChild(clone);
          injectedElements.push(clone);
        }
      });
      
      // Also handle text nodes that might contain scripts (like inline GA code)
      const textContent = settings.header_code.trim();
      if (textContent.includes("<script") && tempDiv.children.length === 0) {
        // If the HTML wasn't parsed as elements, try to inject it directly
        const scriptMatch = textContent.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
        if (scriptMatch) {
          scriptMatch.forEach((scriptHtml) => {
            const tempScript = document.createElement("div");
            tempScript.innerHTML = scriptHtml;
            const scriptEl = tempScript.querySelector("script");
            if (scriptEl) {
              const script = document.createElement("script");
              Array.from(scriptEl.attributes).forEach((attr) => {
                script.setAttribute(attr.name, attr.value);
              });
              if (scriptEl.textContent) {
                script.textContent = scriptEl.textContent;
              }
              script.setAttribute("data-injected", "true");
              document.head.appendChild(script);
              injectedElements.push(script);
            }
          });
        }
      }
    }

    // Inject footer code
    if (settings.footer_code) {
      const footerId = "custom-footer-code";
      let footerContainer = document.getElementById(footerId);
      
      if (!footerContainer) {
        footerContainer = document.createElement("div");
        footerContainer.id = footerId;
        footerContainer.setAttribute("data-injected", "true");
        document.body.appendChild(footerContainer);
        injectedElements.push(footerContainer);
      }
      
      footerContainer.innerHTML = settings.footer_code;
      
      // Execute any scripts in footer
      const scripts = footerContainer.querySelectorAll("script");
      scripts.forEach((script) => {
        const newScript = document.createElement("script");
        Array.from(script.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (script.textContent) {
          newScript.textContent = script.textContent;
        }
        script.parentNode.replaceChild(newScript, script);
      });
    }

    // Cleanup function
    return () => {
      injectedElements.forEach((el) => {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      // Also remove any elements with data-injected attribute
      document.querySelectorAll("[data-injected='true']").forEach((el) => {
        el.remove();
      });
    };
  }, [settings]);

  return null; // This component doesn't render anything visible
};

export default HeadCodeInjector;
